import {
    collection,
    doc,
    setDoc,
    updateDoc,

    getDoc,
    query,
    where,
    serverTimestamp,
    orderBy,
    onSnapshot,
    type Unsubscribe
} from 'firebase/firestore';
import { db } from '@/lib/firebase';
// Use explicit type imports to avoid circular dependency issues if any
import type { CollaborationInvite, User } from '@/types';
import { PlaylistService } from './playlist.service';
import { convertTimestamps, TIMESTAMP_FIELDS } from '@/utils/firestore';

export class InviteService {
    private static readonly COLLECTION = 'invites';

    /**
     * Send an invite to a user
     */
    static async sendInvite(
        playlistId: string,
        playlistName: string,
        inviter: User,
        invitee: User
    ): Promise<string> {
        const inviteRef = doc(collection(db, this.COLLECTION));

        const inviteData: CollaborationInvite = {
            id: inviteRef.id,
            playlistId,
            playlistName,
            inviterId: inviter.id,
            inviterName: inviter.name,
            inviteeEmail: invitee.email,
            inviteeId: invitee.id,
            role: 'editor', // Default role for now
            status: 'pending',
            createdAt: new Date(), // Placeholder for serverTimestamp
            expiresAt: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000) // 7 days expiry
        };

        await setDoc(inviteRef, {
            ...inviteData,
            createdAt: serverTimestamp(),
            expiresAt: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000)
        });

        return inviteRef.id;
    }

    /**
     * Accept an invite
     */
    static async acceptInvite(inviteId: string): Promise<void> {
        const inviteRef = doc(db, this.COLLECTION, inviteId);
        const inviteSnap = await getDoc(inviteRef);

        if (!inviteSnap.exists()) throw new Error('Invite not found');
        const invite = inviteSnap.data() as CollaborationInvite;

        if (invite.status !== 'pending') throw new Error('Invite is not pending');

        // Add to playlist
        await PlaylistService.addCollaborator(invite.playlistId, invite.inviteeId!);

        // Update invite status
        await updateDoc(inviteRef, {
            status: 'accepted',
            respondedAt: serverTimestamp()
        });
    }

    /**
     * Decline an invite
     */
    static async declineInvite(inviteId: string): Promise<void> {
        const inviteRef = doc(db, this.COLLECTION, inviteId);

        await updateDoc(inviteRef, {
            status: 'declined',
            respondedAt: serverTimestamp()
        });
    }

    /**
     * Subscribe to pending invites for a user
     */
    static subscribeToPendingInvites(userId: string, callback: (invites: CollaborationInvite[]) => void): Unsubscribe {
        const q = query(
            collection(db, this.COLLECTION),
            where('inviteeId', '==', userId),
            where('status', '==', 'pending'),
            orderBy('createdAt', 'desc')
        );

        return onSnapshot(q, (snapshot) => {
            const invites = snapshot.docs.map(doc =>
                convertTimestamps(doc.data() as CollaborationInvite, TIMESTAMP_FIELDS.invite as any)
            );
            callback(invites);
        });
    }
}
